<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { debounce } from '@chatwoot/utils';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BroadcastLayout from 'dashboard/components-next/broadcasts/BroadcastLayout.vue';
import BroadcastsAPI from 'dashboard/api/broadcasts';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const AUDIENCE_CHANNELS = ['telegram', 'max', 'vk', 'email', 'website'];

const broadcastId = computed(() =>
  route.params.broadcastId ? Number(route.params.broadcastId) : null
);
const isEdit = computed(() => Boolean(broadcastId.value));

const inboxes = useMapGetter('inboxes/getInboxes');
const uiFlags = useMapGetter('broadcasts/getUIFlags');

const isFetching = ref(false);
const audienceCount = ref(null);
const isPreviewing = ref(false);

const state = reactive({
  title: '',
  content: '',
  abEnabled: false,
  contentVariantB: '',
  channels: [],
  inboxIds: [],
  phoneNumbersText: '',
  onlyVerified: false,
  throttleMs: 0,
  scheduledAt: '',
  // По умолчанию — реальная отправка; тестовый прогон включается явно.
  dryRun: false,
});

const rules = {
  title: { required, minLength: minLength(1) },
  content: { required, minLength: minLength(1) },
};

const v$ = useVuelidate(rules, state);

const isSaving = computed(
  () => uiFlags.value.creatingItem || uiFlags.value.updatingItem
);

const accountInboxes = computed(() => inboxes.value || []);

const channelLabel = channel =>
  t(`BROADCASTS.CHANNELS.${channel.toUpperCase()}`);

// Текст «Номера телефонов» → массив строк: по одному в строке
// или через запятую/точку с запятой/пробел. Нормализация — на бэкенде.
const parsedPhoneNumbers = computed(() =>
  state.phoneNumbersText
    .split(/[\s,;]+/)
    .map(entry => entry.trim())
    .filter(Boolean)
);

const phoneNumbersMessage = computed(() =>
  parsedPhoneNumbers.value.length
    ? t('BROADCASTS.FORM.PHONE_NUMBERS.ONLY_THESE', {
        count: parsedPhoneNumbers.value.length,
      })
    : t('BROADCASTS.FORM.PHONE_NUMBERS.HELP_TEXT')
);

const buildAudience = () => ({
  channels: state.channels,
  inbox_ids: state.inboxIds,
  phone_numbers: parsedPhoneNumbers.value,
  only_verified: state.onlyVerified,
});

// Р11 п.9: предпросмотр показывает не только число, но и выборку получателей.
const audienceSample = ref([]);
const showAudienceSample = ref(false);

const fetchAudiencePreview = async ({ reveal = false } = {}) => {
  isPreviewing.value = true;
  try {
    const { data } = await BroadcastsAPI.audiencePreview(buildAudience());
    audienceCount.value = data.count;
    audienceSample.value = data.sample || [];
    if (reveal) showAudienceSample.value = true;
  } catch (error) {
    audienceCount.value = null;
    audienceSample.value = [];
    useAlert(t('BROADCASTS.FORM.AUDIENCE.PREVIEW_ERROR'));
  } finally {
    isPreviewing.value = false;
  }
};

// Кнопка «Предпросмотр аудитории» — раскрывает список получателей.
const previewAudience = () => fetchAudiencePreview({ reveal: true });

// Р11 п.10: ИИ-генерация текста рассылки из черновика.
const isGenerating = ref(false);

const generateContent = async () => {
  if (!state.content.trim()) {
    useAlert(t('BROADCASTS.FORM.CONTENT.GENERATE_EMPTY'));
    return;
  }
  isGenerating.value = true;
  try {
    const { data } = await BroadcastsAPI.generateContent(state.content);
    if (data.content) state.content = data.content;
    useAlert(t('BROADCASTS.FORM.CONTENT.GENERATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('BROADCASTS.FORM.CONTENT.GENERATE_ERROR')
    );
  } finally {
    isGenerating.value = false;
  }
};

const fetchAudiencePreviewDebounced = debounce(fetchAudiencePreview, 400);

const toggleChannel = channel => {
  const index = state.channels.indexOf(channel);
  if (index === -1) {
    state.channels.push(channel);
  } else {
    state.channels.splice(index, 1);
  }
  fetchAudiencePreviewDebounced();
};

const toggleInbox = inboxId => {
  const index = state.inboxIds.indexOf(inboxId);
  if (index === -1) {
    state.inboxIds.push(inboxId);
  } else {
    state.inboxIds.splice(index, 1);
  }
  fetchAudiencePreviewDebounced();
};

// Точечная рассылка: предпросмотр пересчитываем по мере ввода номеров.
watch(
  () => state.phoneNumbersText,
  () => fetchAudiencePreviewDebounced()
);

const titleError = computed(() =>
  v$.value.title.$error ? t('BROADCASTS.FORM.TITLE.ERROR') : ''
);
const contentError = computed(() =>
  v$.value.content.$error ? t('BROADCASTS.FORM.CONTENT.ERROR') : ''
);

const buildPayload = () => {
  const payload = {
    title: state.title,
    content: state.content,
    throttle_ms: Number(state.throttleMs) || 0,
    dry_run: state.dryRun,
    audience: buildAudience(),
  };

  if (state.abEnabled) {
    payload.content_variant_b = state.contentVariantB;
  }

  if (state.scheduledAt) {
    payload.scheduled_at = new Date(state.scheduledAt).toISOString();
  }

  return payload;
};

const saveDraft = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    if (isEdit.value) {
      await store.dispatch('broadcasts/update', {
        id: broadcastId.value,
        ...buildPayload(),
      });
    } else {
      await store.dispatch('broadcasts/create', buildPayload());
    }
    useAlert(t('BROADCASTS.FORM.SAVE.SUCCESS_MESSAGE'));
    router.push({ name: 'broadcasts_index' });
  } catch (error) {
    useAlert(error?.message || t('BROADCASTS.FORM.SAVE.ERROR_MESSAGE'));
  }
};

const launch = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    let record;
    if (isEdit.value) {
      record = await store.dispatch('broadcasts/update', {
        id: broadcastId.value,
        ...buildPayload(),
      });
    } else {
      record = await store.dispatch('broadcasts/create', buildPayload());
    }
    await store.dispatch('broadcasts/sendNow', record.id);
    useAlert(t('BROADCASTS.FORM.LAUNCH.SUCCESS_MESSAGE'));
    router.push({
      name: 'broadcasts_details',
      params: { broadcastId: record.id },
    });
  } catch (error) {
    useAlert(error?.message || t('BROADCASTS.FORM.LAUNCH.ERROR_MESSAGE'));
  }
};

const hydrateFromRecord = record => {
  if (!record) return;
  state.title = record.title || '';
  state.content = record.content || '';
  state.contentVariantB = record.content_variant_b || '';
  state.abEnabled = record.ab_enabled || Boolean(record.content_variant_b);
  state.channels = record.audience?.channels || [];
  state.inboxIds = record.audience?.inbox_ids || [];
  state.phoneNumbersText = (record.audience?.phone_numbers || []).join('\n');
  state.onlyVerified = record.audience?.only_verified || false;
  state.throttleMs = record.throttle_ms || 0;
  state.dryRun = record.dry_run ?? false;
  if (record.scheduled_at) {
    // datetime-local wants "YYYY-MM-DDTHH:mm"
    state.scheduledAt = new Date(record.scheduled_at)
      .toISOString()
      .slice(0, 16);
  }
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  if (isEdit.value) {
    isFetching.value = true;
    try {
      const record = await store.dispatch('broadcasts/show', broadcastId.value);
      hydrateFromRecord(record);
    } finally {
      isFetching.value = false;
    }
  }
  fetchAudiencePreview();
});
</script>

<template>
  <BroadcastLayout
    :header-title="
      isEdit ? t('BROADCASTS.FORM.EDIT_TITLE') : t('BROADCASTS.FORM.NEW_TITLE')
    "
    :back-url="{ name: 'broadcasts_index' }"
    :is-fetching="isFetching"
  >
    <template #body>
      <form class="flex flex-col gap-5 max-w-2xl" @submit.prevent="saveDraft">
        <Input
          v-model="state.title"
          :label="t('BROADCASTS.FORM.TITLE.LABEL')"
          :placeholder="t('BROADCASTS.FORM.TITLE.PLACEHOLDER')"
          :message="titleError"
          :message-type="titleError ? 'error' : 'info'"
        />

        <div class="flex flex-col gap-1.5">
          <TextArea
            v-model="state.content"
            :label="t('BROADCASTS.FORM.CONTENT.LABEL')"
            :placeholder="t('BROADCASTS.FORM.CONTENT.PLACEHOLDER')"
            :max-length="20000"
            auto-height
            :message="contentError || t('BROADCASTS.FORM.CONTENT.HELP_TEXT')"
            :message-type="contentError ? 'error' : 'info'"
          />
          <!-- Р11 п.10: ИИ дорабатывает черновик в аккуратное сообщение. -->
          <div class="flex justify-end">
            <Button
              type="button"
              variant="faded"
              color="slate"
              size="sm"
              icon="i-lucide-sparkles"
              :label="t('BROADCASTS.FORM.CONTENT.GENERATE_BUTTON')"
              :is-loading="isGenerating"
              :disabled="isGenerating"
              @click="generateContent"
            />
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <label class="flex items-center gap-2">
            <Switch v-model="state.abEnabled" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BROADCASTS.FORM.AB_TEST.LABEL') }}
            </span>
          </label>
          <TextArea
            v-if="state.abEnabled"
            v-model="state.contentVariantB"
            :label="t('BROADCASTS.FORM.CONTENT_VARIANT_B.LABEL')"
            :placeholder="t('BROADCASTS.FORM.CONTENT_VARIANT_B.PLACEHOLDER')"
            :max-length="20000"
            auto-height
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.FORM.CHANNELS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-3">
            <label
              v-for="channel in AUDIENCE_CHANNELS"
              :key="channel"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="state.channels.includes(channel)"
                @change="toggleChannel(channel)"
              />
              {{ channelLabel(channel) }}
            </label>
          </div>
        </div>

        <label class="flex items-center gap-2">
          <Switch v-model="state.onlyVerified" @change="fetchAudiencePreview" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.FORM.ONLY_VERIFIED.LABEL') }}
          </span>
        </label>

        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('BROADCASTS.FORM.INBOXES.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-3">
            <label
              v-for="inbox in accountInboxes"
              :key="inbox.id"
              class="flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                type="checkbox"
                :checked="state.inboxIds.includes(inbox.id)"
                @change="toggleInbox(inbox.id)"
              />
              {{ inbox.name }}
            </label>
          </div>
          <span class="text-xs text-n-slate-11">
            {{
              state.inboxIds.length
                ? t('BROADCASTS.FORM.INBOXES.HELP')
                : t('BROADCASTS.FORM.INBOXES.ANY')
            }}
          </span>
        </div>

        <TextArea
          v-model="state.phoneNumbersText"
          :label="t('BROADCASTS.FORM.PHONE_NUMBERS.LABEL')"
          :placeholder="t('BROADCASTS.FORM.PHONE_NUMBERS.PLACEHOLDER')"
          :max-length="20000"
          auto-height
          :message="phoneNumbersMessage"
          message-type="info"
        />

        <Input
          v-model="state.throttleMs"
          type="number"
          min="0"
          step="1"
          :label="t('BROADCASTS.FORM.THROTTLE.LABEL')"
          :placeholder="t('BROADCASTS.FORM.THROTTLE.PLACEHOLDER')"
          :message="t('BROADCASTS.FORM.THROTTLE.HELP_TEXT')"
        />

        <Input
          v-model="state.scheduledAt"
          type="datetime-local"
          :label="t('BROADCASTS.FORM.SCHEDULED_AT.LABEL')"
          :message="t('BROADCASTS.FORM.SCHEDULED_AT.HELP_TEXT')"
        />

        <div class="flex flex-col gap-1">
          <label class="flex items-center gap-2">
            <Switch v-model="state.dryRun" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('BROADCASTS.FORM.DRY_RUN.LABEL') }}
            </span>
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('BROADCASTS.FORM.DRY_RUN.HELP') }}
          </span>
        </div>

        <div
          class="flex flex-col gap-3 p-3 rounded-lg border border-n-weak bg-n-alpha-2"
        >
          <div class="flex items-center justify-between gap-3">
            <div class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('BROADCASTS.FORM.AUDIENCE.TITLE') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  audienceCount === null
                    ? t('BROADCASTS.FORM.AUDIENCE.UNKNOWN')
                    : t('BROADCASTS.FORM.AUDIENCE.COUNT', {
                        count: audienceCount,
                      })
                }}
              </span>
            </div>
            <Button
              type="button"
              variant="faded"
              color="slate"
              icon="i-lucide-users"
              :label="t('BROADCASTS.FORM.AUDIENCE.PREVIEW_BUTTON')"
              :is-loading="isPreviewing"
              :disabled="isPreviewing"
              @click="previewAudience"
            />
          </div>

          <!-- Р11 п.9: список получателей, кого затронет рассылка. -->
          <div
            v-if="showAudienceSample && audienceSample.length"
            class="flex flex-col divide-y divide-n-weak rounded-lg bg-n-solid-1"
          >
            <div
              v-for="recipient in audienceSample"
              :key="recipient.contact_id"
              class="flex items-center justify-between gap-3 px-3 py-2 text-sm"
            >
              <span class="truncate text-n-slate-12">{{ recipient.name }}</span>
              <span class="shrink-0 text-xs text-n-slate-11">
                {{ recipient.inbox }}
              </span>
            </div>
            <div
              v-if="audienceCount > audienceSample.length"
              class="px-3 py-2 text-xs text-n-slate-11"
            >
              {{
                t('BROADCASTS.FORM.AUDIENCE.AND_MORE', {
                  count: audienceCount - audienceSample.length,
                })
              }}
            </div>
          </div>
          <span
            v-else-if="showAudienceSample"
            class="px-1 text-xs text-n-slate-11"
          >
            {{ t('BROADCASTS.FORM.AUDIENCE.EMPTY') }}
          </span>
        </div>

        <div class="flex items-center gap-3">
          <Button
            type="submit"
            variant="faded"
            color="slate"
            :label="t('BROADCASTS.FORM.SAVE_DRAFT')"
            :is-loading="isSaving"
            :disabled="isSaving"
          />
          <Button
            type="button"
            icon="i-lucide-send"
            :label="t('BROADCASTS.FORM.LAUNCH_BUTTON')"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="launch"
          />
        </div>
      </form>
    </template>
  </BroadcastLayout>
</template>
